import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useChrome } from 'lib/chrome';

import FooterMenu from './FooterMenu';
import Balloon from 'components/Balloon';
import { casinoDirect } from '../apps';
import startButton from 'assets/windowsIcons/start.png';
import sound from 'assets/windowsIcons/690(16x16).png';
import usb from 'assets/windowsIcons/394(16x16).png';
import risk from 'assets/windowsIcons/229(16x16).png';
import messenger from 'assets/windowsIcons/msn.png';
import { fetchMsft, fmtUsd } from 'data/msft';

const getTime = () => {
  const date = new Date();
  let hour = date.getHours();
  let hourPostFix = 'AM';
  let min = date.getMinutes();
  if (hour >= 12) {
    hour -= 12;
    hourPostFix = 'PM';
  }
  if (hour === 0) {
    hour = 12;
  }
  if (min < 10) {
    min = '0' + min;
  }
  return `${hour}:${min} ${hourPostFix}`;
};

function Footer({
  onMouseDownApp,
  apps,
  focusedAppId,
  onMouseDown,
  onClickMenuItem,
}) {
  const chromeTheme = useChrome();
  const [time, setTime] = useState(getTime);
  const [menuOn, setMenuOn] = useState(false);
  const [msft, setMsft] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => fetchMsft().then(x => alive && setMsft(x));
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);
  const menu = useRef(null);
  function toggleMenu() {
    setMenuOn(on => !on);
  }
  function _onMouseDown(e) {
    if (e.target.closest('.footer__window')) return;
    onMouseDown();
  }
  function _onClickMenuItem(name) {
    onClickMenuItem(name);
    setMenuOn(false);
  }
  useEffect(() => {
    const timer = setInterval(() => {
      const newTime = getTime();
      newTime !== time && setTime(newTime);
    }, 1000);
    return () => clearInterval(timer);
  }, [time]);
  useEffect(() => {
    const target = menu.current;
    if (!target) return;
    function onMouseDown(e) {
      if (!target.contains(e.target) && menuOn) setMenuOn(false);
    }
    window.addEventListener('mousedown', onMouseDown);
    return () => window.removeEventListener('mousedown', onMouseDown);
  }, [menuOn]);

  return (
    <Container chromeTheme={chromeTheme} onMouseDown={_onMouseDown}>
      <div className="footer__items left">
        <div ref={menu} className="footer__start__menu">
          {menuOn && <FooterMenu onClick={_onClickMenuItem} />}
        </div>
        <img
          src={startButton}
          alt="start"
          className="footer__start"
          onMouseDown={toggleMenu}
        />
        {[...apps].map(
          app =>
            !app.header.noFooterWindow && (
              <FooterWindow
                key={app.id}
                id={app.id}
                icon={app.header.icon}
                title={app.header.title}
                onMouseDown={onMouseDownApp}
                isFocus={focusedAppId === app.id}
              />
            ),
        )}
      </div>

      <div className="footer__items right">
        <div
          className="footer__msft"
          title="MSFT.exe — the stock the internet is quoted in"
          onClick={() =>
            window.dispatchEvent(new CustomEvent('ic:open', { detail: 'Msft' }))
          }
        >
          MSFT {msft ? fmtUsd(msft.price) : '——'}
        </div>
        <img
          className="footer__icon footer__icon--btn"
          src={messenger}
          alt="Windows Messenger"
          title="Windows Messenger"
          onClick={() =>
            window.dispatchEvent(
              new CustomEvent('ic:open', { detail: 'Windows Messenger' }),
            )
          }
        />
        <img className="footer__icon" src={sound} alt="" />
        <img className="footer__icon" src={usb} alt="" />
        <img className="footer__icon" src={risk} alt="" />
        <div style={{ position: 'relative', width: 0, height: 0 }}>
          {/* The antivirus balloon is a desktop gag. On a phone it parks itself over the
              stake box and the FLIP button for fifteen seconds, and on a direct casino link
              nobody asked for the desktop at all. */}
          {!casinoDirect() && window.innerWidth >= 800 ? <Balloon /> : null}
        </div>
        <div className="footer__time">{time}</div>
      </div>
    </Container>
  );
}

function FooterWindow({ id, icon, title, onMouseDown, isFocus }) {
  function _onMouseDown() {
    onMouseDown(id);
  }
  return (
    <div
      onMouseDown={_onMouseDown}
      className={`footer__window ${isFocus ? 'focus' : 'cover'}`}
    >
      <img className="footer__icon" src={icon} alt={title} />
      <div className="footer__text">{title}</div>
    </div>
  );
}

const Container = styled.footer`
  height: 30px;
  /* chrome preset override — see lib/chrome.js. Painted after the baked gradient below. */
  &&& {
    background: ${p => (p.chromeTheme ? p.chromeTheme.taskbar : 'none')};
  }
  background: linear-gradient(
    to bottom,
    #73891c 0,
    #a9c82d 3%,
    #c5e932 6%,
    #c8ea40 10%,
    #c5e934 12%,
    #bfe526 15%,
    #b9df21 18%,
    #b5da1f 20%,
    #b4d91e 23%,
    #b5db1c 38%,
    #b9e01f 54%,
    #bde420 86%,
    #bae11f 89%,
    #b3d81d 92%,
    #a2c419 95%,
    #8ba916 98%
  );
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  display: flex;
  .footer__items.left {
    height: 100%;
    flex: 1;
    overflow: hidden;
  }
  .footer__items.right {
    background-color: #c0ef05;
    flex-shrink: 0;
    /* chrome preset override for the Start button */
    &&& {
      background: ${p => (p.chromeTheme ? p.chromeTheme.start : 'none')};
      color: ${p => (p.chromeTheme ? p.chromeTheme.startText : 'inherit')};
    }
    background: linear-gradient(
      to bottom,
      #99bd08 1%,
      #c1ee0e 6%,
      #caf713 10%,
      #c3f00e 14%,
      #c0ed0d 19%,
      #c1f007 63%,
      #c7f707 81%,
      #c4f309 88%,
      #c1ee0c 91%,
      #bce70e 94%,
      #b3dc0e 97%,
      #a5ce04 100%
    );
    border-left: 1px solid #92b30c;
    box-shadow: inset 1px 0 1px #d1ff18;
    padding: 0 10px;
    margin-left: 10px;
  }
  .footer__items {
    display: flex;
    align-items: center;
  }
  .footer__msft {
    font-family: 'Lucida Console', 'Courier New', monospace;
    font-size: 10px;
    letter-spacing: 1px;
    color: #0a0c04;
    background: rgba(255, 255, 255, 0.35);
    border: 1px solid rgba(0, 0, 0, 0.25);
    border-radius: 2px;
    padding: 1px 6px;
    margin-right: 8px;
    cursor: pointer;
    white-space: nowrap;
    &:hover {
      background: rgba(255, 255, 255, 0.6);
    }
    @media (max-width: 520px) {
      display: none;
    }
  }
  .footer__start {
    height: 100%;
    margin-right: 10px;
    position: relative;
    &:hover {
      filter: brightness(105%);
    }
    &:active {
      pointer-events: none;
      filter: brightness(85%);
    }
  }
  .footer__start__menu {
    position: absolute;
    left: 0;
    box-shadow: 2px 4px 2px rgba(0, 0, 0, 0.5);
    bottom: 100%;
  }
  .footer__window {
    flex: 1;
    max-width: 150px;
    /* Dark ink, not white. XP puts white on a blue taskbar button; on a lime one white text sits
       at roughly 1.5:1 against #d1f837 and is genuinely unreadable — Kris reported exactly this.
       Near-black olive reads on both the bright idle button and the darker focused one, and it
       matches the Start button's own text colour. */
    color: #1d2a00;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.35);
    border-radius: 2px;
    margin-top: 2px;
    padding: 0 8px;
    height: 22px;
    font-size: 11px;
    background-color: #d1f837;
    box-shadow: inset -1px 0px rgba(0, 0, 0, 0.3),
      inset 1px 1px 1px rgba(255, 255, 255, 0.2);
    position: relative;
    display: flex;
    align-items: center;
  }
  .footer__icon {
    height: 15px;
    width: 15px;
  }
  .footer__icon--btn {
    cursor: pointer;
    margin-right: 4px;
  }
  .footer__text {
    position: absolute;
    left: 27px;
    right: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .footer__window.cover:hover {
    background-color: #ddff53;
    box-shadow: inset -1px 0px rgba(0, 0, 0, 0.3),
      inset 1px 1px 1px rgba(255, 255, 255, 0.2);
  }
  .footer__window.cover:before {
    display: block;
    content: '';
    position: absolute;
    left: -2px;
    top: -2px;
    width: 10px;
    height: 1px;
    border-bottom-right-radius: 50%;
    box-shadow: 2px 2px 3px rgba(255, 255, 255, 0.5);
  }
  .footer__window.cover:hover:active {
    background-color: #9bbb1a;
    box-shadow: inset 0 0 1px 1px rgba(0, 0, 0, 0.3),
      inset 1px 0 1px rgba(0, 0, 0, 0.7);
  }
  .footer__window.focus:hover {
    background-color: #d0f830;
  }
  .footer__window.focus:hover:active {
    background-color: #9bbb1a;
  }
  .footer__window.focus {
    background-color: #9bbb1a;
    box-shadow: inset 0 0 1px 1px rgba(0, 0, 0, 0.2),
      inset 1px 0 1px rgba(0, 0, 0, 0.7);
  }
  .footer__time {
    margin: 0 5px;
    /* same problem as the taskbar buttons: white on lime disappears */
    color: #1d2a00;
    font-size: 11px;
    font-weight: normal;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.3);
  }
`;

export default Footer;
